import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Editorial-plain closed-testing application page.
// Uses the existing Anokina Studio palette (paper #EFE6D5, espresso #2A1D14, bronze #8B6F4B)
// and stays deliberately quiet — no hero, no cards, no progress bar.

const REAL_ORDERS_OPTIONS = [
  { value: "regular",   label: "Да, регулярно" },
  { value: "sometimes", label: "Иногда"        },
  { value: "not_yet",   label: "Пока нет"      },
];

const FEEDBACK_OPTIONS = [
  { value: "yes", label: "Да" },
  { value: "no",  label: "Нет" },
];

const TRY_LIST = [
  "создать и провести реальный заказ",
  "пересчитать рецепт под нужную форму и размер",
  "рассчитать себестоимость изделия",
  "учесть упаковку, работу и дополнительные расходы",
  "посмотреть расчёты заказа и этапы производства",
  "познакомиться с рабочим процессом Мастера заказа",
];

function EyebrowTitle({ eyebrow, title }) {
  return (
    <header className="mb-8">
      <div
        className="text-[10.5px] tracking-[0.3em] uppercase mb-3"
        style={{ color: "#8B6F4B" }}
        data-testid="mo-testing-eyebrow"
      >
        {eyebrow}
      </div>
      <h1
        className="leading-[1.1] font-serif"
        style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14", fontSize: "clamp(28px, 4.6vw, 40px)" }}
        data-testid="mo-testing-title"
      >
        {title}
      </h1>
    </header>
  );
}

function KeySignature() {
  // Subtle inline SVG mark — brand key sign as a quiet signature.
  return (
    <div className="mt-14 flex justify-center opacity-60" aria-hidden="true">
      <img
        src="/favicon-48x48.png"
        alt=""
        width={22}
        height={22}
        className="opacity-80"
        style={{ filter: "saturate(0.85)" }}
      />
    </div>
  );
}

function StatusChip({ children, testid = "mo-testing-status-chip" }) {
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11.5px] tracking-[0.14em] uppercase mb-6"
      style={{ background: "#F1E4CC", color: "#5B4326" }}
      data-testid={testid}
    >
      <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "#8B6F4B" }} />
      {children}
    </div>
  );
}

function PillButton({ children, onClick, testid, tone = "primary" }) {
  const styles = tone === "secondary"
    ? { background: "transparent", color: "#5B4326", boxShadow: "inset 0 0 0 1px #C7A97A" }
    : { background: "#4A3728", color: "#F7EFE0" };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="h-11 px-6 rounded-full text-[13px]"
      style={styles}
    >
      {children}
    </button>
  );
}

function formatExpiry(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(d);
  } catch { return ""; }
}

function SubmittedState({ headline }) {
  const nav = useNavigate();
  return (
    <section data-testid="mo-testing-submitted-state" className="mt-2">
      <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title={headline} />
      <StatusChip>На рассмотрении</StatusChip>
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
        {headline === "Заявка отправлена" ? (
          <>
            <p className="mb-3">Спасибо. Мы получили Вашу заявку.</p>
            <p>
              Сейчас формируется первая закрытая группа кондитеров для тестирования
              Мастера заказа. Если Вы войдёте в неё, мы свяжемся с Вами и сообщим,
              когда будет открыт доступ на 24&nbsp;часа.
            </p>
          </>
        ) : (
          <p>
            Мы получили Вашу заявку на участие в закрытом тестировании
            Мастера заказа.
          </p>
        )}
      </div>
      <div className="mt-10">
        <PillButton testid="mo-testing-back-to-hall" onClick={() => nav("/hall")}>
          Вернуться в Anokina Studio
        </PillButton>
      </div>
      <KeySignature />
    </section>
  );
}

function DeclinedState() {
  const nav = useNavigate();
  return (
    <section data-testid="mo-testing-declined-state" className="mt-2">
      <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title="Набор первой группы завершён" />
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
        <p>
          Спасибо за интерес к тестированию Мастера заказа.
          Мы сохранили Вашу заявку и будем рады пригласить Вас в следующие этапы тестирования.
        </p>
      </div>
      <div className="mt-10">
        <PillButton testid="mo-testing-back-to-hall" onClick={() => nav("/hall")}>
          Вернуться в Anokina Studio
        </PillButton>
      </div>
      <KeySignature />
    </section>
  );
}

function AcceptedState() {
  const nav = useNavigate();
  return (
    <section data-testid="mo-testing-accepted-state" className="mt-2">
      <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title="Вы вошли в тестовую группу" />
      <StatusChip>Принята</StatusChip>
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
        <p className="mb-3">
          Ваша заявка одобрена. Когда Вы будете готовы начать тестирование, откройте Мастер заказа.
        </p>
        <p>
          Ваши 24&nbsp;часа тестового доступа начнутся с первого входа в Мастер заказа.
        </p>
      </div>
      <div className="mt-10">
        <PillButton testid="mo-testing-start-button" onClick={() => nav("/master-order")}>
          Начать тестирование
        </PillButton>
      </div>
      <KeySignature />
    </section>
  );
}

function ActiveTestState({ application }) {
  const nav = useNavigate();
  const expires = formatExpiry(application?.test_access_expires_at);
  return (
    <section data-testid="mo-testing-active-state" className="mt-2">
      <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title="Тестовый доступ открыт" />
      <StatusChip>Доступ открыт</StatusChip>
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
        <p className="mb-2">Ваш доступ к Мастеру заказа открыт.</p>
        {expires && (
          <p className="text-[13.5px]" style={{ color: "#5B4326" }} data-testid="mo-testing-expiry">
            Доступ до&nbsp;{expires}
          </p>
        )}
      </div>
      <div className="mt-10">
        <PillButton testid="mo-testing-open-master-order" onClick={() => nav("/master-order")}>
          Открыть Мастер заказа
        </PillButton>
      </div>
      <KeySignature />
    </section>
  );
}

function CompletedState() {
  const nav = useNavigate();
  return (
    <section data-testid="mo-testing-completed-state" className="mt-2">
      <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title="Тестирование завершено" />
      <StatusChip>Тест завершён</StatusChip>
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
        <p className="mb-3">Спасибо, что познакомились с Мастером заказа.</p>
        <p>
          Для нас особенно важна Ваша честная обратная связь. Расскажите, каким оказался инструмент
          именно в реальной работе — что было удобно, что оказалось непонятным, чего Вам не хватило
          и хотели бы Вы пользоваться Мастером заказа дальше.
        </p>
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <PillButton testid="mo-testing-share-feedback" onClick={() => nav("/master-order-testing/feedback")}>
          Поделиться впечатлениями
        </PillButton>
        <PillButton testid="mo-testing-back-to-hall" tone="secondary" onClick={() => nav("/hall")}>
          Вернуться в Главный холл
        </PillButton>
      </div>
      <KeySignature />
    </section>
  );
}

function FeedbackReceivedState() {
  const nav = useNavigate();
  return (
    <section data-testid="mo-testing-feedback-received-state" className="mt-2">
      <EyebrowTitle eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА" title="Спасибо за обратную связь" />
      <StatusChip>Отзыв получен</StatusChip>
      <div className="max-w-[62ch] text-[15px] leading-[1.65]" style={{ color: "#3E2E20" }}>
        <p>
          Ваши впечатления помогут нам сделать Мастер заказа ещё понятнее и удобнее
          для реальной работы кондитера.
        </p>
      </div>
      <div className="mt-10">
        <PillButton testid="mo-testing-back-to-hall" onClick={() => nav("/hall")}>
          Вернуться в Anokina Studio
        </PillButton>
      </div>
      <KeySignature />
    </section>
  );
}

export default function MasterOrderTesting() {
  const nav = useNavigate();
  const { user, initializing } = useAuth();

  const [loadingExisting, setLoadingExisting] = useState(true);
  const [existing, setExisting] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  // Form state
  const [socialUrl, setSocialUrl] = useState("");
  const [realOrders, setRealOrders] = useState("");
  const [simplifyText, setSimplifyText] = useState("");
  const [feedbackOk, setFeedbackOk] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Public-facing tab title.
  useEffect(() => {
    const prev = document.title;
    document.title = "Закрытое тестирование Мастера заказа — Anokina Studio";
    return () => { document.title = prev; };
  }, []);

  // Auth guard with return-path handoff.
  useEffect(() => {
    if (initializing) return;
    if (!user) {
      try { sessionStorage.setItem("post_login_redirect", "/master-order-testing"); } catch {}
      nav("/login", { replace: true });
    }
  }, [initializing, user, nav]);

  // Fetch existing application (if any) once authed.
  useEffect(() => {
    if (initializing || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/master-order-testing/applications/mine");
        if (!cancelled) setExisting(data || null);
      } catch (e) {
        // 401 handled by ProtectedRoute; anything else — leave form usable, but surface once.
        if (!cancelled) toast.error(formatApiError(e) || "Не удалось загрузить состояние заявки");
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initializing, user]);

  const readOnlyName = useMemo(() => (user?.display_name || user?.name || "").trim(), [user]);

  const validate = () => {
    const errs = {};
    if (!socialUrl.trim() || socialUrl.trim().length < 3) errs.socialUrl = "Укажите ссылку на Вашу страницу.";
    if (!realOrders) errs.realOrders = "Пожалуйста, выберите вариант.";
    if (!feedbackOk) errs.feedbackOk = "Пожалуйста, выберите вариант.";
    if (!consent)   errs.consent    = "Необходимо согласие на обработку данных.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/master-order-testing/applications", {
        social_url: socialUrl.trim(),
        real_orders_status: realOrders,
        simplify_work_text: simplifyText.trim() || null,
        feedback_commitment: feedbackOk,
        consent_accepted: true,
      });
      setExisting(data);
      setJustSubmitted(true);
      toast.success("Заявка отправлена");
    } catch (err) {
      // If a duplicate slipped through (network retries, second tab), server returns 409
      // with the existing application — show the returning-user state.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.application) {
        setExisting(detail.application);
        setJustSubmitted(false);
        toast.info("Ваша заявка уже была получена ранее");
      } else {
        toast.error(formatApiError(err) || "Не удалось отправить заявку");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (initializing || !user) return null;

  // Existing application → dispatch on server-authoritative status.
  if (!loadingExisting && existing) {
    const st = existing.status;
    let stateEl = null;
    if (st === "declined") {
      stateEl = <DeclinedState />;
    } else if (st === "accepted") {
      stateEl = <AcceptedState />;
    } else if (st === "active_test") {
      stateEl = <ActiveTestState application={existing} />;
    } else if (st === "completed_test") {
      stateEl = <CompletedState />;
    } else if (st === "feedback_received") {
      stateEl = <FeedbackReceivedState />;
    } else {
      // "new" — original submitted / returning state.
      stateEl = <SubmittedState headline={justSubmitted ? "Заявка отправлена" : "Ваша заявка получена"} />;
    }
    return <PageFrame>{stateEl}</PageFrame>;
  }

  if (loadingExisting) {
    return (
      <PageFrame>
        <div className="py-16 text-[13px]" style={{ color: "#8B6F4B" }} data-testid="mo-testing-loading">
          Загружаем состояние заявки…
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <EyebrowTitle
        eyebrow="ANOKINA STUDIO · МАСТЕР ЗАКАЗА"
        title="Закрытое тестирование Мастера заказа"
      />

      <div
        className="max-w-[62ch] text-[15px] leading-[1.7]"
        style={{ color: "#3E2E20" }}
        data-testid="mo-testing-intro"
      >
        <p className="mb-4">
          Я хочу пригласить кондитеров первыми зайти внутрь Мастера заказа Anokina&nbsp;Studio и попробовать его до официального запуска.
        </p>
        <p className="mb-4">
          Если Вы войдёте в тестовую группу, мы откроем Вам доступ на 24&nbsp;часа. За это время Вы сможете спокойно познакомиться с инструментами Мастера заказа, попробовать их на своих рецептах и рабочих сценариях и понять, насколько такой формат подходит именно Вам.
        </p>
        <p>
          Для нас здесь особенно важна честная обратная связь: удобно ли Вам работать внутри, всё ли понятно с первого раза, какие функции действительно полезны, чего не хватает, что кажется лишним и хотели бы Вы пользоваться таким инструментом в своей работе дальше.
        </p>
      </div>

      <section className="mt-12 max-w-[62ch]" data-testid="mo-testing-try-section">
        <div
          className="text-[10.5px] tracking-[0.3em] uppercase mb-3"
          style={{ color: "#8B6F4B" }}
        >
          Что можно будет попробовать
        </div>
        <ul className="text-[14.5px] leading-[1.8]" style={{ color: "#3E2E20" }}>
          {TRY_LIST.map((line) => (
            <li key={line} className="flex gap-3">
              <span
                aria-hidden="true"
                className="mt-[10px] inline-block h-[3px] w-[10px] rounded-full flex-shrink-0"
                style={{ background: "#C7A97A" }}
              />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      <form
        onSubmit={onSubmit}
        className="mt-14 max-w-[62ch] space-y-8"
        data-testid="mo-testing-form"
        noValidate
      >
        {/* Field 1 — read-only name */}
        <div>
          <Label>Имя</Label>
          <div
            data-testid="mo-testing-name-readonly"
            className="mt-2 h-11 px-4 flex items-center rounded-md text-[14px]"
            style={{
              background: "#F7EFE0",
              color: "#3E2E20",
              boxShadow: "inset 0 0 0 1px #E4D3B0",
            }}
          >
            {readOnlyName || "—"}
          </div>
        </div>

        {/* Field 2 — social url */}
        <div>
          <Label required>Ссылка на Вашу рабочую страницу / соцсеть</Label>
          <input
            data-testid="mo-testing-social-input"
            type="text"
            inputMode="url"
            autoComplete="url"
            value={socialUrl}
            onChange={(e) => setSocialUrl(e.target.value)}
            placeholder="https://instagram.com/…"
            className="mt-2 w-full h-11 px-4 rounded-md text-[14px] bg-white outline-none focus:ring-2"
            style={{ boxShadow: "inset 0 0 0 1px #E4D3B0", color: "#2A1D14" }}
          />
          <HelperText>Instagram, Telegram или другая площадка, где можно посмотреть Ваши работы.</HelperText>
          {fieldErrors.socialUrl && <FieldError>{fieldErrors.socialUrl}</FieldError>}
        </div>

        {/* Field 3 — real orders status */}
        <RadioGroup
          testid="mo-testing-real-orders"
          label="Вы сейчас работаете с реальными заказами?"
          required
          value={realOrders}
          options={REAL_ORDERS_OPTIONS}
          onChange={setRealOrders}
          error={fieldErrors.realOrders}
        />

        {/* Field 4 — simplify text */}
        <div>
          <Label>Что сейчас больше всего хотелось бы упростить в работе с заказами?</Label>
          <textarea
            data-testid="mo-testing-simplify-textarea"
            value={simplifyText}
            onChange={(e) => setSimplifyText(e.target.value)}
            rows={4}
            className="mt-2 w-full px-4 py-3 rounded-md text-[14px] bg-white outline-none focus:ring-2 resize-y"
            style={{ boxShadow: "inset 0 0 0 1px #E4D3B0", color: "#2A1D14" }}
          />
        </div>

        {/* Field 5 — feedback commitment */}
        <RadioGroup
          testid="mo-testing-feedback"
          label="Если Вы войдёте в тестовую группу, готовы познакомиться с Мастером заказа и после тестирования поделиться с нами честной обратной связью?"
          required
          value={feedbackOk}
          options={FEEDBACK_OPTIONS}
          onChange={setFeedbackOk}
          error={fieldErrors.feedbackOk}
        />

        {/* Consent */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="mo-testing-consent-checkbox"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#4A3728]"
            />
            <span className="text-[14px] leading-[1.55]" style={{ color: "#3E2E20" }}>
              Я согласен(на) на обработку данных, указанных в заявке, для отбора и участия в закрытом тестировании Мастера заказа.
            </span>
          </label>
          <HelperText>
            Данные используются только для отбора участников и связи по тестированию.
            {" "}Подробнее — в{" "}
            <span
              data-testid="mo-testing-privacy-link"
              className="underline underline-offset-2"
              style={{ color: "#5B4326" }}
            >
              Политике конфиденциальности
            </span>.
          </HelperText>
          {fieldErrors.consent && <FieldError>{fieldErrors.consent}</FieldError>}
        </div>

        <div className="pt-2">
          <button
            data-testid="mo-testing-submit-button"
            type="submit"
            disabled={submitting}
            className="h-11 px-8 rounded-full text-[13.5px] transition-opacity"
            style={{
              background: "#4A3728",
              color: "#F7EFE0",
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? "wait" : "pointer",
            }}
          >
            {submitting ? "Отправляем…" : "Подать заявку"}
          </button>
        </div>
      </form>

      <KeySignature />
    </PageFrame>
  );
}

// ---------------- primitives (scoped to this page) ----------------

function PageFrame({ children }) {
  return (
    <main
      className="min-h-screen"
      style={{ background: "#EFE6D5" }}
      data-testid="mo-testing-page"
    >
      <div className="mx-auto px-5 sm:px-10 py-12 sm:py-16" style={{ maxWidth: 880 }}>
        {children}
      </div>
    </main>
  );
}

function Label({ children, required }) {
  return (
    <div
      className="text-[10.5px] tracking-[0.22em] uppercase"
      style={{ color: "#8B6F4B" }}
    >
      {children}{required && <span aria-hidden="true"> ·</span>}
    </div>
  );
}

function HelperText({ children }) {
  return (
    <div className="mt-2 text-[12.5px] leading-[1.5]" style={{ color: "#8B6F4B" }}>
      {children}
    </div>
  );
}

function FieldError({ children }) {
  return (
    <div className="mt-2 text-[12.5px]" style={{ color: "#8B3E2A" }} role="alert">
      {children}
    </div>
  );
}

function RadioGroup({ testid, label, required, options, value, onChange, error }) {
  return (
    <div data-testid={testid}>
      <Label required={required}>{label}</Label>
      <div className="mt-3 flex flex-col gap-2">
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <label
              key={opt.value}
              data-testid={`${testid}-option-${opt.value}`}
              className="flex items-center gap-3 cursor-pointer h-11 px-4 rounded-md text-[14px]"
              style={{
                background: selected ? "#F1E4CC" : "#FFFFFF",
                color: "#2A1D14",
                boxShadow: `inset 0 0 0 1px ${selected ? "#C7A97A" : "#E4D3B0"}`,
              }}
            >
              <input
                type="radio"
                name={testid}
                checked={selected}
                onChange={() => onChange(opt.value)}
                className="accent-[#4A3728]"
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}
